import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  People as PeopleIcon,
  Description as DescriptionIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import API_BASE_URL from '../config/api';

const Dashboard = () => {
  const [stats, setStats] = useState({
    employees: 0,
    payslips: 0,
    loading: true,
    error: null,
  });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [employeesRes, payslipsRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/employees?limit=1`),
        axios.get(`${API_BASE_URL}/api/payslips?limit=1`),
      ]);

      setStats({
        employees: employeesRes.data.pagination?.total || 0,
        payslips: payslipsRes.data.pagination?.total || 0,
        loading: false,
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
      setStats({
        employees: 0,
        payslips: 0,
        loading: false,
        error: 'Failed to load statistics',
      });
    }
  };

  if (stats.loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (stats.error) {
    return <Alert severity="error">{stats.error}</Alert>;
  }

  const statCards = [
    {
      title: 'Total Employees',
      value: stats.employees,
      icon: <PeopleIcon sx={{ fontSize: 48 }} />,
      color: '#6366f1',
      gradient: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
    },
    {
      title: 'Generated Payslips',
      value: stats.payslips,
      icon: <DescriptionIcon sx={{ fontSize: 48 }} />,
      color: '#10b981',
      gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    },
    {
      title: 'Active Periods',
      value: 'Current',
      icon: <TrendingUpIcon sx={{ fontSize: 48 }} />,
      color: '#f59e0b',
      gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    },
  ];

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, color: '#1e293b' }}>
          Dashboard
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Overview of your payroll system
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {statCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Card
              sx={{
                height: '100%',
                borderRadius: 2,
                background: card.gradient,
                color: 'white',
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: -20,
                  right: -20,
                  opacity: 0.1,
                }}
              >
                {card.icon}
              </Box>
              <CardContent sx={{ position: 'relative', p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box
                    sx={{
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      borderRadius: 2,
                      p: 1.5,
                      mr: 2,
                    }}
                  >
                    {card.icon}
                  </Box>
                  <Box>
                    <Typography variant="h3" sx={{ fontWeight: 700, mb: 0.5 }}>
                      {typeof card.value === 'number' ? card.value.toLocaleString() : card.value}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      {card.title}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default Dashboard;
